import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, CircularProgress, Grid } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import ToolBarPlaylist from './ToolBarPlaylist';
import {
  apiFormOptionsProvider,
  apiGetPlaylists, PlaylistState,
} from '../store/playlistSlice';
import { styled } from '@mui/material/styles';
import { Heading } from './common/Heading';
import { EditPlaylistDialog } from './modal/EditPlaylistDialog';
import axios from 'axios';
import { getApiBaseUrl } from '../utils/env';
import { PlaylistTable } from './playlist-detail/PlaylistTable';
import { PlaylistAPIData, PlaylistFormData } from './types/playlist';
import { OrganisationContext } from '../App';
import { RootState } from '../index';

const TableRoot = styled(Grid)`
    margin-top: 80px;
    flex-basis: 100%;
    margin-bottom: 20px;
`;

export default function Playlist() {
  const dispatch = useDispatch();
  const { selectedOrganisation, organisations } = useContext(OrganisationContext);
  const selectedOrg = organisations.find(({ domain }) => domain === selectedOrganisation);
  const newPlaylistData = {
    format: 'vertical',
    refreshRateInMins: 60,
    orgId: selectedOrg?.id || null,
    type: 1,
    isActive: true,
    isRandomize: false,
    name: null,
    uuid: null,
    mrssUsername: null,
    mrssPassword: null,
    mrssUrl: null,
    iabCategories: [],
    iabCategoriesExclude: false,
    trendCategories: [],
    playlistPublisher: [],
    playlistPublisherExclude: false,
    geoCountries: [],
    geoCountriesExclude: false,
    dailyNotificationAtHour: 0,
    emailAddress: [],
    enableNotification: false,
    maxVideoApproved: 10,
    publisherRightsholderIds: [],
    asaApproved: false,
  };
  const {
    playlists,
    playlistTypes,
    iabCategories,
    playlistRightsholder,
    playlistPublisherRightsholder,
    geoCountries,
    trendCategories,
    busy
  } = useSelector<RootState, PlaylistState>((state) => state.playlist);
  const [openPlaylistDialog, setOpenPlaylistDialog] = React.useState<boolean>(false);
  const [playlistData, setPlaylistData] = useState<PlaylistFormData>(newPlaylistData);

  useEffect(() => {
    if (!selectedOrganisation) return;
    dispatch<any>(
      apiFormOptionsProvider(selectedOrganisation)
    );
    dispatch(apiGetPlaylists({
      publisher: selectedOrganisation,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganisation]);

  const savePlaylist = async (data: PlaylistFormData) => {
    const action = data.uuid ? 'patch' : 'post';

    // default value
    const payload: Record<string, any> = {
      uuid: data.uuid,
      name: data.name,
      org_id: selectedOrg?.id,
      playlist_type_id: data.type,
      format: data.format,

      refresh_rate_in_mins: null,

      is_active: data.isActive,
      is_randomize: data.isRandomize,

      mrss_url: null,
      mrss_username: null,
      mrss_password: null,

      trend_categories: [],

      iab_categories: [],
      iab_categories_exclude: false,
      playlist_publisher: [],
      playlist_publisher_exclude: false,
      playlist_geo_country: [],
      playlist_geo_country_exclude: false,

      enabled_email_notification: data.enableNotification,
      notification_email_target: data.emailAddress || null,
      notification_daily_at_hour: data.dailyNotificationAtHour,
      max_approved_videos: data.maxVideoApproved,
      publisher_rightsholder_ids: data.publisherRightsholderIds || [],
      asa_approved: data.asaApproved,
    };

    const { playlistType: playlistLabel } = playlistTypes.find(({ id }) => id === data.type) || { playlistType: '' };

    switch (playlistLabel.toLowerCase()) {
      case 'manual':
        // no op
        break;
      case 'mrss':
        payload['mrss_url'] = data.mrssUrl;
        payload['mrss_username'] = data.mrssUsername;
        payload['mrss_password'] = data.mrssPassword;
        break;
      case 'deep match':
        payload['refresh_rate_in_mins'] = data.refreshRateInMins;
        payload['iab_categories'] = data.iabCategories;
        payload['iab_categories_exclude'] = data.iabCategoriesExclude;
        payload['playlist_publisher'] = data.playlistPublisher;
        payload['playlist_publisher_exclude'] = data.playlistPublisherExclude;
        payload['playlist_geo_country'] = data.geoCountries;
        payload['playlist_geo_country_exclude'] = data.geoCountriesExclude;
        break;
      case 'trend match':
        payload['trend_categories'] = data.trendCategories;
        payload['playlist_geo_country'] = data.geoCountries;
        break;
      case 'recently approved':
        payload['publisher_rightsholder_ids'] = data.publisherRightsholderIds;
        break;
      default:
        alert('Invalid playlist type');
    }

    if (data.type)
      await axios[action](`${getApiBaseUrl()}/playlist/${selectedOrganisation}`, payload);
    setOpenPlaylistDialog(false);
    dispatch(apiGetPlaylists({
      publisher: selectedOrganisation,
    }));
  };

  const removePlaylist = async (uuid: string) => {
    await axios.delete(`${getApiBaseUrl()}/playlist/${selectedOrganisation}/${uuid}`);
    dispatch(apiGetPlaylists({
      publisher: selectedOrganisation,
    }));
  };
  return <React.Fragment>
    <ToolBarPlaylist
      playlist={undefined}
      creative={undefined}
    />
    <TableRoot>
      <Grid container justifyContent={'space-between'} alignItems={'center'}>
        <Grid item>
          <Heading>Playlists</Heading>
        </Grid>
        <Grid item>
          <Button
            variant={'contained'}
            onClick={() => {
              setPlaylistData(newPlaylistData);
              setOpenPlaylistDialog(true);
            }}
          >
            Add new
          </Button>
        </Grid>
      </Grid>
    </TableRoot>
    {playlists.length > 0 && (
      <PlaylistTable
        onEditPlaylist={(data: PlaylistAPIData) => {
          setPlaylistData({
            format: data.format,
            type: data.playlist_type_id,
            isActive: data.is_active,
            isRandomize: data.is_randomize,
            name: data.name,
            mrssPassword: data.mrss_password,
            mrssUrl: data.mrss_url,
            mrssUsername: data.mrss_username,
            orgId: data.org_id,
            refreshRateInMins: data.refresh_rate_in_mins,
            uuid: data.uuid,
            iabCategories: data.iab_categories || [],
            iabCategoriesExclude: data.iab_categories_exclude,
            trendCategories: data.keyword_trend_category_id || [],
            playlistPublisher: (data.playlist_publisher || []).map(String),
            playlistPublisherExclude: data.playlist_publisher_exclude,
            geoCountries: (data.playlist_geo_country || []),
            geoCountriesExclude: (data.playlist_geo_country_exclude || false),
            emailAddress: data.notification_email_target || [],
            dailyNotificationAtHour: data.notification_daily_at_hour || 0,
            enableNotification: data.enabled_email_notification,
            maxVideoApproved: data.max_approved_videos,
            publisherRightsholderIds: data.publisher_rightsholder_ids || [],
            asaApproved: data.asa_approved
          });
          setOpenPlaylistDialog(true);
        }}
        onRemovePlaylist={async (data: PlaylistAPIData) => {
          if (!window.confirm(`Are you sure you want to remove this '${data.name}' playlist?`)) {
            return;
          }
          await removePlaylist(data.uuid as string);
        }}
        playlists={playlists}
      />
    )}
    {playlists.length === 0 && busy && (
      <Grid container justifyContent={'center'} alignItems={'center'}
            style={{ minHeight: 'calc(100vh - 100px)', marginTop: '100px' }}>
        <Grid item>
          <CircularProgress style={{ padding: '5px' }}/>
        </Grid>
      </Grid>)}
    {openPlaylistDialog && (
      <EditPlaylistDialog
        playlistTypes={playlistTypes}
        trendCategories={trendCategories}
        data={playlistData}
        handleClose={() => setOpenPlaylistDialog(false)}
        open={Boolean(openPlaylistDialog)}
        onSave={savePlaylist}
        iabCategories={iabCategories}
        playlistRightsholder={playlistRightsholder}
        geoCountries={geoCountries}
        playlistPublisherRightsholder={playlistPublisherRightsholder}
      />
    )}
  </React.Fragment>;
}
